import { useFormState } from "@/lib/Components/Form/Hooks/useFormState";
import { useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { supplierOfficeRecordQuery } from "@/app/Offices/GraphQL/supplierOfficeRecordQuery";
import { TripCard } from "@/app/Cities/Trips/Components/TripCard";
import { RelocationFormValues } from "@/app/Relocations/Utils/getDefaultRelocationValues";
import { TriangleAlertIcon } from "lucide-react";

export function RelocationTripCard() {
  const { values } = useFormState<RelocationFormValues>();

  const deliveryOfficeId = values.deliveryOfficeIds?.[0];

  const { data: departureData, isLoading: departureLoading } = useGqlQuery(
    supplierOfficeRecordQuery,
    { id: values.departureOffice?.connect ?? "" },
    { enabled: !!values.departureOffice?.connect },
  );

  const { data: deliveryData, isLoading: deliveryLoading } = useGqlQuery(
    supplierOfficeRecordQuery,
    { id: deliveryOfficeId ?? "" },
    { enabled: !!deliveryOfficeId },
  );

  // Additional destinations are the rest of deliveryOfficeIds (after the first one)
  const additionalDestinationIds = values.deliveryOfficeIds?.slice(1) ?? [];

  // Fetch up to 3 additional destinations
  const { data: additional1 } = useGqlQuery(
    supplierOfficeRecordQuery,
    { id: additionalDestinationIds[0] ?? "" },
    { enabled: !!additionalDestinationIds[0] },
  );
  const { data: additional2 } = useGqlQuery(
    supplierOfficeRecordQuery,
    { id: additionalDestinationIds[1] ?? "" },
    { enabled: !!additionalDestinationIds[1] },
  );
  const { data: additional3 } = useGqlQuery(
    supplierOfficeRecordQuery,
    { id: additionalDestinationIds[2] ?? "" },
    { enabled: !!additionalDestinationIds[2] },
  );

  if (!deliveryOfficeId || !values.departureOffice?.connect) {
    return null;
  }

  if (departureLoading || deliveryLoading) {
    return (
      <div className="w-full overflow-hidden rounded-md border border-gray-200">
        <div className="flex h-[240px] items-center justify-center bg-gray-100">
          <span className="text-sm text-gray-500">Loading map...</span>
        </div>
        <div className="h-[50px] bg-gray-50" />
      </div>
    );
  }

  const departureCity = departureData?.supplierOffice.city;
  const deliveryCity = deliveryData?.supplierOffice.city;

  if (!departureCity || !deliveryCity) {
    return null;
  }

  // Both offices pinned to the same city means there is no route to draw and a
  // 0km trip downstream — almost always the wrong city on one of the offices.
  if (departureCity.id === deliveryCity.id) {
    return (
      <div className="flex w-full items-start gap-3 rounded-md border-2 border-amber-400 bg-amber-50 p-4">
        <TriangleAlertIcon className="mt-0.5 size-6 shrink-0 text-amber-600" />
        <div>
          <p className="text-base font-semibold text-amber-900">
            Both locations are in {departureCity.name}.
          </p>
          <p className="mt-1 text-sm text-amber-800">
            A relocation needs a different pickup and drop-off city. Check the
            city set on each location — the location name is free text and does
            not have to match its city.
          </p>
        </div>
      </div>
    );
  }

  const additionalCities = [
    additional1?.supplierOffice.city,
    additional2?.supplierOffice.city,
    additional3?.supplierOffice.city,
  ].filter((city): city is NonNullable<typeof city> => !!city);

  return (
    <TripCard
      cityAId={departureCity.id}
      cityBId={deliveryCity.id}
      fallbackCityA={departureCity}
      fallbackCityB={deliveryCity}
      additionalCities={additionalCities}
      measurement={values.measurement}
    />
  );
}
